import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Timeline } from "primereact/timeline";
import { Tooltip } from "primereact/tooltip";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ReportActionLogEntryResponse } from "../../../../client";
import { getReportActionLog } from "../../../../api/routes";
import { PantheonAvatarWithoutMenu } from "../../../../components/avatar/Avatar";
import { useUsers } from "../../data";
import { displayNameForUser } from "../../../../util";
import sharedStyles from "./shared.module.css";
import styles from "./SubmissionHistoryDetail.module.css";
import {
  ACTION_TYPE_ICONS,
  ACTION_TYPE_SEVERITY,
  ActionType,
  TimelineGroup,
  TimelineItem,
  buildTimelineItems,
  formatLabel,
  formatMetadata,
  formatTaskError,
  formatTimestamp,
  taskLogId,
  taskLogsUrl,
} from "./submissionHistoryUtils";

const TIMELINE_MARKER_COLORS: Record<ActionType, string> = {
  EMAIL_RECEIVED: "#2196F3",
  REPORT_CREATED: "#4CAF50",
  EMAIL_ADDED_TO_REPORT: "#2196F3",
  EMAIL_REJECTED_INVALID_SENDER: "#f44336",
  EMAIL_REJECTED_DUPLICATE: "#FF9800",
  EMAIL_REJECTED_NO_ATTACHMENTS: "#FF9800",
  EMAIL_REJECTED_REPLY_THREAD: "#FF9800",
  UPDATE_REPORT: "#009688",
  STATUS_CHANGED: "#3F51B5",
  ASSIGNEE_CHANGED: "#3F51B5",
  UPDATE_ADDRESSES: "#009688",
  INTEGRATION_JOB_ENQUEUED: "#9E9E9E",
  INTEGRATION_JOB_STARTED: "#FF9800",
  INTEGRATION_JOB_COMPLETED: "#4CAF50",
  INTEGRATION_JOB_FAILED: "#f44336",
  CLEARANCE_EMAIL_SENT: "#4CAF50",
  QUOTE_INDICATION_DRAFTED: "#3F51B5",
  REFERRAL_DRAFTED: "#3F51B5",
  ATTACHMENT_UPLOADED: "#607D8B",
  ATTACHMENT_DELETED: "#FF9800",
  COMMENT_ADDED: "#673AB7",
  COMMENT_EDITED: "#673AB7",
  COMMENT_DELETED: "#FF9800",
  BOR_CREATED: "#FF9800",
  BOR_FINALIZED: "#4CAF50",
  BOR_RESCINDED: "#2196F3",
  BOR_EMAIL_SENT: "#4CAF50",

  CAT_RUN_ACTIVATED: "#FF9800",
  QUEUE_PRIORITY_CHANGED: "#3F51B5",
};

const CopyTaskIdButton = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false);
  const tooltipId = `copy-${value}`;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (e.g. insecure context); nothing to do.
    }
  };
  return (
    <>
      <Tooltip target={`#${tooltipId}`} showDelay={500} />
      <button
        type="button"
        id={tooltipId}
        className={styles.timelineGroupTaskCopy}
        onClick={onCopy}
        aria-label="Copy task ID"
        data-pr-tooltip={copied ? "Copied!" : "Copy task ID"}
      >
        <i className={copied ? "pi pi-check" : "pi pi-copy"} />
      </button>
    </>
  );
};

export const SubmissionHistoryDetail = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const users = useUsers();

  const { data: detail, isLoading } = useQuery({
    queryKey: ["submission-history-detail", reportId],
    queryFn: () => getReportActionLog(reportId ?? ""),
    enabled: !!reportId,
  });

  const actions = detail?.actions ?? [];
  const companyName = detail?.company_name;
  const emailSubject = detail?.email_subject;

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const timelineItems = useMemo(
    () => [...buildTimelineItems(detail?.actions ?? [])].reverse(),
    [detail]
  );

  const findUser = (
    userId: string | null
  ): { name: string; imageUrl: string | null | undefined } | undefined => {
    if (!userId) return undefined;
    const user = (users?.data?.all_company_users ?? []).find(
      (u) => u.id === userId
    );
    return {
      name: displayNameForUser(user) ?? userId,
      imageUrl: user?.profile_image_url,
    };
  };

  const markerTemplate = (item: TimelineItem) => {
    const actionType =
      item.kind === "group" ? item.actionType : item.entry.action_type;
    const color = TIMELINE_MARKER_COLORS[actionType] ?? "#9E9E9E";
    return (
      <span
        className={styles.timelineMarker}
        style={{ backgroundColor: color }}
      >
        <i className={ACTION_TYPE_ICONS[actionType] ?? "pi pi-circle"} />
      </span>
    );
  };

  const singleContent = (item: ReportActionLogEntryResponse) => {
    const meta = formatMetadata(item.metadata);
    const user = findUser(item.user_id);
    return (
      <div className={styles.timelineCard}>
        <div className={styles.timelineCardHeader}>
          <Tag
            value={formatLabel(item.action_type)}
            severity={ACTION_TYPE_SEVERITY[item.action_type]}
            icon={ACTION_TYPE_ICONS[item.action_type]}
          />
          <div className={styles.timelineCardMeta}>
            {user && (
              <PantheonAvatarWithoutMenu
                label={user.name}
                imageUrl={user.imageUrl}
                size="small"
              />
            )}
            <span className={styles.timelineCardTimestamp}>
              {formatTimestamp(item.timestamp)}
            </span>
          </div>
        </div>
        {meta && <div className={styles.timelineCardDetails}>{meta}</div>}
      </div>
    );
  };

  const groupContent = (group: TimelineGroup) => {
    const expanded = expandedKeys.has(group.key);
    const count = group.entries.length;
    const rangeLabel =
      group.start === group.end
        ? formatTimestamp(group.start)
        : `${formatTimestamp(group.start)} – ${formatTimestamp(group.end)}`;
    return (
      <div className={styles.timelineCard}>
        <div className={styles.timelineCardHeader}>
          <Tag
            value={formatLabel(group.actionType)}
            severity={ACTION_TYPE_SEVERITY[group.actionType]}
            icon={ACTION_TYPE_ICONS[group.actionType]}
          />
          <span className={styles.timelineCardTimestamp}>{rangeLabel}</span>
        </div>
        <button
          type="button"
          className={styles.timelineGroupToggle}
          onClick={() => toggleExpanded(group.key)}
          aria-expanded={expanded}
        >
          <i
            className={expanded ? "pi pi-chevron-down" : "pi pi-chevron-right"}
          />
          {group.integrationType} · {count} {count === 1 ? "task" : "tasks"}
        </button>
        {expanded && (
          <div className={styles.timelineGroupTasks}>
            {group.entries.map((entry) => {
              const logId = taskLogId(entry.metadata);
              const errorText = formatTaskError(entry.metadata);
              const errorTooltipId = `task-error-${entry.id}`;
              return (
                <div key={entry.id} className={styles.timelineGroupTask}>
                  {logId && (
                    <>
                      <a
                        className={styles.timelineGroupTaskLink}
                        href={taskLogsUrl(logId)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        logs
                      </a>
                      <CopyTaskIdButton value={logId} />
                    </>
                  )}
                  {errorText && (
                    <>
                      <Tooltip target={`#${errorTooltipId}`} showDelay={500} />
                      <span
                        id={errorTooltipId}
                        className={styles.timelineGroupTaskError}
                        data-pr-tooltip={errorText}
                      >
                        {errorText}
                      </span>
                    </>
                  )}
                  <span className={styles.timelineGroupTaskTime}>
                    {formatTimestamp(entry.timestamp)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const contentTemplate = (item: TimelineItem) =>
    item.kind === "group" ? groupContent(item) : singleContent(item.entry);

  const cardTitle = companyName
    ? `${companyName} — Report Timeline`
    : emailSubject
      ? `${emailSubject} — Email Timeline`
      : "Event Timeline";

  return (
    <div className={sharedStyles.page}>
      <Card title={cardTitle}>
        {isLoading ? (
          <div className={sharedStyles.centered}>
            <ProgressSpinner />
          </div>
        ) : actions.length === 0 ? (
          <div className={sharedStyles.emptyState}>
            No events found for this report.
          </div>
        ) : (
          <div className={styles.timelineScrollContainer}>
            <Timeline
              value={timelineItems}
              marker={markerTemplate}
              content={contentTemplate}
              align="right"
              className={styles.timeline}
            />
          </div>
        )}
      </Card>
    </div>
  );
};
